<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    :title="`ルール${isEdit ? '編集' : '追加'}`"
    :sub-title="`NAT(IP Masquerade) ${$filter.enumTo(
      zone.srcZoneType,
      'zoneType'
    )}/${zone.srcZoneName} → ${$filter.enumTo(zone.dstZoneType, 'zoneType')}/${
      zone.dstZoneName
    }`"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- ID -->
                <v-row>
                  <v-col>
                    <template v-if="isEdit">
                      <app-label-group
                        label="ID"
                        :value="form.policyId"
                      ></app-label-group>
                    </template>
                    <template v-else>
                      <validation-provider
                        name="policyId"
                        v-model="form.policyId"
                        label="ID"
                        :rules="{
                          required: true,
                          numeric: true,
                          is_not_zero_padding: true,
                          between: { min: 1001, max: 9999 },
                          duplicate: { list: existsPolicyIds },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="ID"
                          :msg="$filter.message('info_policy_id')"
                          placement="end"
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </template>
                  </v-col>
                </v-row>
                <!-- 送信元 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="srcAddressList"
                        label="送信元アドレス"
                        v-model="form.srcAddressList"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage }"
                      >
                        <app-badge
                          item="送信元アドレス"
                          :isInfo="false"
                          placement="end"
                          :isRequired="true"
                        ></app-badge>
                        <app-multi-select
                          v-bind="componentField"
                          :options="srcAddresses"
                          :multiple="true"
                          :custom-label="addressLabel"
                          track-by="ipAddressId"
                          :sort-orders="[
                            ['ipAddressName', 'asc'],
                            ['ipAddress', 'asc'],
                          ]"
                          group-label="label"
                          group-values="values"
                          :closeOnSelect="false"
                          :group-select="true"
                          selectGroupLabel="全てを選択します"
                          deselectGroupLabel="全ての選択を取り消します"
                          :error-messages="errorMessage"
                        >
                          <template #tag="{ option, remove }">
                            <multi-select-tag :option="option" :remove="remove">
                              <div class="multiselect-tbody">
                                <p class="v-col-3 pa-0">
                                  {{ option.ipAddress }}
                                </p>
                                <p class="v-col-9 pa-0">
                                  {{
                                    $filter.emptyTo(option.ipAddressName, "-")
                                  }}
                                </p>
                              </div>
                            </multi-select-tag>
                          </template>

                          <template #option="{ option }">
                            <template v-if="option.$isLabel">{{
                              option.$groupLabel
                            }}</template>
                            <div v-else class="multiselect-tbody">
                              <p class="v-col-3 pa-0">{{ option.ipAddress }}</p>
                              <p class="v-col-8 pa-0">
                                {{ $filter.emptyTo(option.ipAddressName, "-") }}
                              </p>
                            </div>
                          </template>
                        </app-multi-select>
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 宛先 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <app-badge
                        item="宛先アドレス"
                        msg="アドレスを選択する場合は、１つ以上指定してください。"
                        placement="end"
                        :isRequired="radioDstAddress === 'SELECT'"
                      ></app-badge>
                      <v-radio-group
                        v-model="radioDstAddress"
                        density="compact"
                        hide-details
                      >
                        <v-radio value="ANY" label="any" />
                        <v-radio
                          value="SELECT"
                          :disabled="internetAddresses.length === 0"
                          label="アドレス"
                        />
                      </v-radio-group>
                      <template v-if="radioDstAddress === 'SELECT'">
                        <!-- 宛先アドレス選択 -->
                        <validation-provider
                          name="dstAddressList"
                          v-model="form.dstAddressList"
                          label="宛先アドレス"
                          :rules="{ required: true }"
                          v-slot="{ componentField, errorMessage }"
                        >
                          <app-multi-select
                            v-bind="componentField"
                            :options="dstAddresses"
                            :multiple="true"
                            :custom-label="addressLabel"
                            track-by="ipAddressId"
                            :sort-orders="[
                              ['ipAddressName', 'asc'],
                              ['ipAddress', 'asc'],
                            ]"
                            group-label="label"
                            group-values="values"
                            :closeOnSelect="false"
                            :group-select="true"
                            selectGroupLabel="全てを選択します"
                            deselectGroupLabel="全ての選択を取り消します"
                            :error-messages="errorMessage"
                          >
                            <template #tag="{ option, remove }">
                              <multi-select-tag
                                :option="option"
                                :remove="remove"
                              >
                                <div class="multiselect-tbody">
                                  <p class="v-col-3 pa-0">
                                    {{ option.ipAddress }}
                                  </p>
                                  <p class="v-col-9 pa-0">
                                    {{
                                      $filter.emptyTo(option.ipAddressName, "-")
                                    }}
                                  </p>
                                </div>
                              </multi-select-tag>
                            </template>

                            <template #option="{ option }">
                              <template v-if="option.$isLabel">{{
                                option.$groupLabel
                              }}</template>
                              <div v-else class="multiselect-tbody">
                                <p class="v-col-3 pa-0">
                                  {{ option.ipAddress }}
                                </p>
                                <p class="v-col-8 pa-0">
                                  {{
                                    $filter.emptyTo(option.ipAddressName, "-")
                                  }}
                                </p>
                              </div>
                            </template>
                          </app-multi-select>
                        </validation-provider>
                      </template>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- IP Pool -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="ipPoolList"
                        label="IP Pool"
                        v-model="form.ipPoolList"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage }"
                      >
                        <app-badge
                          item="IP Pool"
                          :isInfo="false"
                          :isRequired="true"
                        ></app-badge>
                        <app-multi-select
                          v-bind="componentField"
                          :options="ipPoolOptions"
                          :multiple="true"
                          :custom-label="ipPoolLabel"
                          track-by="ipPoolSeq"
                          :sort-orders="[['ipPoolName', 'asc']]"
                          group-label="label"
                          group-values="values"
                          :closeOnSelect="false"
                          :group-select="true"
                          selectGroupLabel="全てを選択します"
                          deselectGroupLabel="全ての選択を取り消します"
                          :error-messages="errorMessage"
                        >
                          <template #tag="{ option, remove }">
                            <multi-select-tag :option="option" :remove="remove">
                              <div class="multiselect-tbody">
                                <p class="v-col-3 pa-0">
                                  {{ option.ipPoolName }}
                                </p>
                                <p class="v-col-3 pa-0">
                                  {{ option.globalIpAddress }}
                                </p>
                                <p>
                                  {{ $filter.emptyTo(option.description, "-") }}
                                </p>
                              </div>
                            </multi-select-tag>
                          </template>
                          <template #option="{ option }">
                            {{ option.$groupLabel }}
                            <div class="multiselect-tbody">
                              <p class="v-col-3 pa-0">
                                {{ option.ipPoolName }}
                              </p>
                              <p class="v-col-3 pa-0">
                                {{ option.globalIpAddress }}
                              </p>
                              <p class="v-col-5 pa-0" v-if="option.ipPoolName">
                                {{ $filter.emptyTo(option.description, "-") }}
                              </p>
                            </div>
                          </template>
                        </app-multi-select>
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 有効/無効 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <v-row>
                        <v-col cols="6">
                          <v-row>
                            <v-col cols="4">
                              <app-badge
                                item="有効/無効"
                                :isRequired="false"
                                :isInfo="false"
                              />
                            </v-col>
                            <v-col>
                              <v-radio-group
                                v-model="form.isPolicyStatus"
                                density="comfortable"
                                hide-details
                              >
                                <v-radio :value="true" label="有効" />
                                <v-radio :value="false" label="無効" />
                              </v-radio-group>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="description"
                        v-model="form.description"
                        label="備考"
                        :rules="{ max: { length: 20 } }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="備考"
                          msg="20文字以内"
                          :isRequired="false"
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn variant="flat" type="submit" color="primary">保存</v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { GetPolicyType1 } from "@/apis/NwPolicyApi";
import cloneDeep from "lodash/cloneDeep";
import type { IpPool } from "@/apis/IpPoolApi";
import MultiSelectTag from "@/components/MultiSelectTag.vue";
import type { Type1IpMasqueradePolicy } from "@/modals/applianceContractSetting/type1/policy/ipMasquerade/InternetType1PolicyRuleIpMasqViewRD.vue";
import type {
  Type1ExtraInfo,
  Type1InternetAddress,
  Type1PrivateInfo,
  Type1PrivateLine,
  Type1PrivateSite,
  Type1Site,
} from "@/apis/Type1SiteApi";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import { VueFinalModal } from "vue-final-modal";
import InternetType1PolicyRuleIpMasqConfirm from "@/modals/applianceContractSetting/type1/policy/ipMasquerade/InternetType1PolicyRuleIpMasqConfirm.vue";

export default defineComponent({
  name: "InternetType1PolicyRuleIpMasqEdit",
  components: { MultiSelectTag },
  props: {
    /** ゾーン */
    zone: {
      type: Object as PropType<ZoneRelationItem>,
      required: true,
    },
    /** 変更対象ポリシー */
    policy: {
      type: Object as PropType<Type1IpMasqueradePolicy>,
      default: undefined,
    },
    /** type1ポリシー全体 */
    type1Policy: {
      type: Object as PropType<GetPolicyType1>,
      required: true,
    },
  },
  data() {
    const clonePolicy = cloneDeep(this.policy);
    const form = (clonePolicy ?? {
      srcAddressList: [],
      dstAddressList: [],
      ipPoolList: [],
      isPolicyStatus: true,
      policyCategoryInfo: "CUSTOM_POLICY",
    }) as Type1IpMasqueradePolicy;

    return {
      form,
      // 宛先（any/アドレス）
      radioDstAddress: (form.dstAddressList.length === 0 ? "ANY" : "SELECT") as
        | "ANY"
        | "SELECT",
      // インターネット（アドレス一覧）
      internetAddresses: [] as Type1InternetAddress[],
      // IP Pool一覧
      ipPools: [] as IpPool[],
      extraOrPrivateSiteAddresses: [] as
        | Type1ExtraInfo["siteList"][0]["addressList"]
        | Type1PrivateInfo["lineList"][0]["siteList"][0]["addressList"],

      isLoaded: false,
    };
  },
  computed: {
    /** true: 編集, false: 新規 */
    isEdit(): boolean {
      return !!this.policy;
    },
    /** 送信元アドレスのSelectの選択肢 */
    srcAddresses(): {
      label: string;
      values:
        | Type1ExtraInfo["siteList"][0]["addressList"]
        | Type1PrivateInfo["lineList"][0]["siteList"][0]["addressList"];
    }[] {
      return this.extraOrPrivateSiteAddresses.length !== 0
        ? [
            {
              label: "全選択",
              values: this.extraOrPrivateSiteAddresses,
            },
          ]
        : [];
    },
    /** 宛先アドレスのSelectの選択肢 */
    dstAddresses(): {
      label: string;
      values: Type1InternetAddress[];
    }[] {
      return [
        {
          label: "全選択",
          values: this.internetAddresses,
        },
      ];
    },
    /** IP PoolのSelectの選択肢 */
    ipPoolOptions(): {
      label: string;
      values: IpPool[];
    }[] {
      return this.ipPools.length !== 0
        ? [
            {
              label: "全選択",
              values: this.ipPools,
            },
          ]
        : [];
    },
    /** 既存のtype4ポリシーID一覧 */
    existsPolicyIds(): string[] {
      const p = this.type1Policy;
      return [
        ...(p.policyUtmType1?.accessPointToInternetList ?? []).flatMap(
          (v) => v.policyList
        ),
        ...(p.policyUtmType1?.internetToAccessPointList ?? []).flatMap(
          (v) => v.policyList
        ),
        ...(p.policyIfwType1?.accessPointToInternetList ?? []).flatMap(
          (v) => v.policyList
        ),
        ...(p.policyIfwType1?.internetToAccessPointList ?? []).flatMap(
          (v) => v.policyList
        ),
        ...(p.policyIPMasqueradeType1List ?? []).flatMap((v) => v.policyList),
        ...(p.policyNatType1List ?? []).flatMap((v) => v.policyList),
      ].map((e) => e.policyId);
    },
    internetZoneId(): string {
      // dstはInternentゾーン
      return this.zone.dstZoneId;
    },
  },
  async mounted() {
    // 選択肢などの必要情報を取得
    if (this.zone.srcZoneType === "PRIVATE") {
      // IPアドレスで昇順ソートするために、プライベートサイト情報を一時保存
      this.extraOrPrivateSiteAddresses = (
        await this.$api.type1SiteZone.getType1PrivateList()
      ).lineList
        .flatMap((v: Type1PrivateLine) => v.siteList)
        .flatMap((v: Type1PrivateSite) => v.addressList)
        .filter((v: Type1InternetAddress) => v.zoneId === this.zone.srcZoneId)
        .sortBy(["ipAddress", "asc"]);
    } else {
      // IPアドレス、IPアドレス名で昇順ソートするために、エクストラサイト情報を一時保存
      this.extraOrPrivateSiteAddresses = (
        await this.$api.type1SiteZone.getType1ExtraList()
      ).siteList
        .flatMap((v: Type1Site) => v.addressList)
        .filter((v: Type1InternetAddress) => v.zoneId === this.zone.srcZoneId)
        .sortBy(["ipAddress", "asc"]);
    }
    const internetSite = await this.$api.type1SiteZone.getType1InternetList();
    // zoneIdが一致しているアドレスリストを表示
    this.internetAddresses = internetSite.addressList
      .filter((v: Type1InternetAddress) => v.zoneId === this.internetZoneId)
      .sortBy(["ipAddress", "asc"]);
    this.ipPools = (await this.$api.ipPool.getIpPoolType1()).ipPoolList;

    this.isLoaded = true;
  },
  methods: {
    /** ルール編集の完了 */
    async submit() {
      const policy = {
        ...this.form,
        srcAddressList: this.form.srcAddressList,
        dstAddressList:
          this.radioDstAddress === "ANY" ? [] : this.form.dstAddressList,
      };
      await this.$modal.show(InternetType1PolicyRuleIpMasqConfirm, { policy });
      (
        this.$refs.modal as typeof VueFinalModal & {
          ok: (e: Type1IpMasqueradePolicy) => void;
        }
      ).ok(policy);
    },
    /** アドレス選択肢ラベル名 */
    addressLabel({
      ipAddressName,
      ipAddress,
    }:
      | Type1InternetAddress
      | Type1ExtraInfo["siteList"][0]["addressList"][0]
      | Type1PrivateInfo["lineList"][0]["siteList"][0]["addressList"][0]): string {
      return `${ipAddressName}  ${ipAddress}`;
    },
    /** IP Poolラベル名（検索用） */
    ipPoolLabel({ ipPoolName, globalIpAddress, description }: IpPool) {
      return `${ipPoolName} ${globalIpAddress} ${description}`;
    },
  },
});
</script>

<style scoped lang="scss">
:deep(.multiselect__tag) {
  display: block;
}
</style>
